// Microbe RTS — world-space camera for the portrait, one-thumb RTS view.
//
// This is the ONLY thing that knows how world coordinates map to the screen. Both the renderer
// and the DOM UI go through screenToWorld/worldToScreen so a tap always resolves to the same
// world point the renderer drew. The math mirrors the existing microbe/world.ts follow rig
// (setTransform(dpr) → translate(W/2,H/2) → scale(zoom) → translate(-camX,-camY)); the inverse
// is (screen - W/2)/zoom + cam. Smoothing is frame-rate independent (1 - e^(-k·dt), like world.ts).
//
// All balance-ish tunables (zoom clamp, lerp gains, edge margin) come from config.CAMERA; only
// the neutral start-zoom guess is a local visual default.

import { WORLD, CAMERA } from '../config.js';
import type { Vec } from '../types.js';

/** The camera surface consumed by render + ui. World units in, screen (CSS px) out. */
export interface Camera {
  /** Current camera CENTER in world coordinates (live object; read-only intent). */
  readonly pos: Vec;
  /** Current zoom (world→screen scale). Assigning sets the smoothed target, clamped. */
  zoom: number;
  /** Viewport size in CSS px. */
  readonly viewW: number;
  readonly viewH: number;
  /** Update viewport (CSS px) after a resize; re-clamps within bounds. */
  resize(w: number, h: number): void;
  /** Advance smoothing toward the follow/zoom targets. Call once per rendered frame. */
  update(dt: number): void;
  /** Smoothly recenter on a world point (eased by CAMERA.camLerp via update). */
  follow(target: Vec): void;
  /** Instantly recenter on a world point (no easing). */
  jumpTo(worldPos: Vec): void;
  /** Drag-pan by a screen-space delta (CSS px). Immediate. */
  pan(dxScreen: number, dyScreen: number): void;
  /** Multiply zoom by `factor`, keeping the world point under `centroid` (screen px) fixed. */
  pinchZoom(factor: number, centroid: Vec): void;
  /** Pinch-zoom convenience for the input layer: (centroidX, centroidY, factor). */
  zoomAt(cx: number, cy: number, factor: number): void;
  /** Smoothly recenter on a world point (e.g. the recenter FAB); no-op if omitted. */
  recenter(target?: Vec): void;
  /** Re-clamp the camera so the viewport stays within WORLD + CAMERA.edgeMargin. */
  clampToBounds(): void;
  /** Screen (CSS px, element-local) → world. Accepts a Vec or raw (x, y). */
  screenToWorld(p: Vec | number, y?: number): Vec;
  /** World → screen (CSS px, element-local). */
  worldToScreen(p: Vec): Vec;
}

function clampNum(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}
function clampZoom(z: number): number {
  return clampNum(z, CAMERA.zoomMin, CAMERA.zoomMax);
}

/**
 * Build a camera bound to a canvas. Starts centered on the map at a comfortable portrait zoom;
 * callers typically jumpTo their Home right after mount.
 */
export function createCamera(canvas: HTMLCanvasElement): Camera {
  // Live position (the object handed out as `pos`) + the eased target.
  const posV: Vec = { x: WORLD.width / 2, y: WORLD.height * 0.38 };
  const targetV: Vec = { x: posV.x, y: posV.y };
  let zoom = clampZoom(0.6); // local visual default; overridden by first follow/jump
  let targetZoom = zoom;
  let viewW = canvas.clientWidth || window.innerWidth || 1;
  let viewH = canvas.clientHeight || window.innerHeight || 1;

  function screenToWorld(p: Vec | number, y?: number): Vec {
    const sx = typeof p === 'number' ? p : p.x;
    const sy = typeof p === 'number' ? (y ?? 0) : p.y;
    return { x: (sx - viewW / 2) / zoom + posV.x, y: (sy - viewH / 2) / zoom + posV.y };
  }
  function worldToScreen(p: Vec): Vec {
    return { x: (p.x - posV.x) * zoom + viewW / 2, y: (p.y - posV.y) * zoom + viewH / 2 };
  }

  // Clamp a world-space center so the viewport doesn't drift past WORLD ± edgeMargin. If the
  // world is smaller than the view on an axis (very zoomed out), center that axis instead.
  function clampVec(v: Vec): void {
    const halfW = viewW / 2 / zoom;
    const halfH = viewH / 2 / zoom;
    const m = CAMERA.edgeMargin;
    const minX = halfW - m;
    const maxX = WORLD.width - halfW + m;
    v.x = minX > maxX ? WORLD.width / 2 : clampNum(v.x, minX, maxX);
    const minY = halfH - m;
    const maxY = WORLD.height - halfH + m;
    v.y = minY > maxY ? WORLD.height / 2 : clampNum(v.y, minY, maxY);
  }
  function clampToBounds(): void {
    clampVec(posV);
    clampVec(targetV);
  }

  return {
    get pos(): Vec {
      return posV;
    },
    get zoom(): number {
      return zoom;
    },
    set zoom(z: number) {
      targetZoom = clampZoom(z);
    },
    get viewW(): number {
      return viewW;
    },
    get viewH(): number {
      return viewH;
    },
    resize(w: number, h: number): void {
      viewW = w > 0 ? w : viewW;
      viewH = h > 0 ? h : viewH;
      clampToBounds();
    },
    update(dt: number): void {
      const d = dt > 0 ? dt : 0;
      const kp = 1 - Math.exp(-CAMERA.camLerp * d);
      const kz = 1 - Math.exp(-CAMERA.zoomLerp * d);
      posV.x += (targetV.x - posV.x) * kp;
      posV.y += (targetV.y - posV.y) * kp;
      zoom += (targetZoom - zoom) * kz;
      clampToBounds();
    },
    follow(target: Vec): void {
      targetV.x = target.x;
      targetV.y = target.y;
      clampVec(targetV);
    },
    jumpTo(worldPos: Vec): void {
      targetV.x = worldPos.x;
      targetV.y = worldPos.y;
      posV.x = worldPos.x;
      posV.y = worldPos.y;
      clampToBounds();
    },
    pan(dxScreen: number, dyScreen: number): void {
      const wx = dxScreen / zoom;
      const wy = dyScreen / zoom;
      posV.x -= wx;
      posV.y -= wy;
      targetV.x -= wx;
      targetV.y -= wy;
      clampToBounds();
    },
    pinchZoom(factor: number, centroid: Vec): void {
      const before = screenToWorld(centroid);
      zoom = clampZoom(zoom * factor);
      targetZoom = zoom;
      // Re-anchor so the same world point stays under the centroid at the new zoom.
      posV.x = before.x - (centroid.x - viewW / 2) / zoom;
      posV.y = before.y - (centroid.y - viewH / 2) / zoom;
      targetV.x = posV.x;
      targetV.y = posV.y;
      clampToBounds();
    },
    zoomAt(cx: number, cy: number, factor: number): void {
      this.pinchZoom(factor, { x: cx, y: cy });
    },
    recenter(target?: Vec): void {
      if (target) this.follow(target);
    },
    clampToBounds,
    screenToWorld,
    worldToScreen,
  };
}
